'use client';

import TrashIcon from '@/assets/svgs/trash-can.svg';
import { inter600 } from '@/config/fonts';
import IconContainer from '@/shared/components/IconContainer/IconContainer';
import classNames from 'classnames';
import React from 'react';

interface ConfirmDeleteDialogProps<T = Record<string, unknown>> {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmButtonText?: string;
  items?: T[];
  itemRenderer?: (item: T) => React.ReactNode;
  maxDisplayItems?: number;
  loading?: boolean;
}

export function ConfirmDeleteDialog<T = Record<string, unknown>>({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText = 'Delete',
  items = [],
  itemRenderer,
  maxDisplayItems = 5,
  loading = false,
}: ConfirmDeleteDialogProps<T>) {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="bg-black-1000/40 absolute inset-0 z-0" onClick={handleCancel} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-xl rounded-lg bg-white shadow-xl">
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex h-[84px] items-center justify-between px-6 pt-6">
            <div className="flex items-center justify-center gap-4">
              <IconContainer
                icon={<TrashIcon className="h-5 w-5" />}
                className="h-11 w-11 bg-red-50 text-red-600"
              />
              <div className={classNames('text-xl', inter600.className)}>{title}</div>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-4 px-6 pb-4">
            <div className="text-gray-700">{message}</div>

            {/* List items being deleted */}
            {items.length > 0 && itemRenderer && (
              <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                <div className="p-3">
                  <div className="mb-2 text-sm font-medium text-gray-700">
                    Items to be deleted:
                  </div>
                  <div className="space-y-2">
                    {items.slice(0, maxDisplayItems).map((item, index) => (
                      <div key={index}>{itemRenderer(item)}</div>
                    ))}
                    {items.length > maxDisplayItems && (
                      <div className="py-2 text-center text-sm text-gray-500">
                        ... and {items.length - maxDisplayItems} more items
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCancel}
                disabled={loading}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-error"
                onClick={handleConfirm}
                disabled={loading}>
                {loading ? 'Deleting...' : confirmButtonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
